import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Alert, Snackbar } from '@mui/material';

import adminNotificationSocket from '../services/adminNotificationSocket';
import supportChatService from '../services/supportChatService';


const SupportChatNotificationContext = createContext(null);
const SOUND_STORAGE_KEY = 'supportChatSoundAlertsEnabled';
const TOAST_DEBOUNCE_MS = 2500;
const SOUND_DUPLICATE_GUARD_MS = 500;
const SUPPORT_CHAT_ADMIN_SOUND_ENABLED =
  String(process.env.REACT_APP_SUPPORT_CHAT_ADMIN_SOUND_ENABLED ?? 'true').toLowerCase() === 'true';


const canUseSupportChatNotifications = (user, token) => {
  if (!token || !user) {
    return false;
  }

  return Boolean(user.is_staff || ['admin', 'staff'].includes(user.role));
};


export const SupportChatNotificationProvider = ({ children }) => {
  const user = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);
  const [unreadCount, setUnreadCount] = useState(0);
  const [socketStatus, setSocketStatus] = useState('idle');
  const [lastSupportEvent, setLastSupportEvent] = useState(null);
  const [sessionUnreadCounts, setSessionUnreadCounts] = useState({});
  const [soundAlertsEnabled, setSoundAlertsEnabledState] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const latestUserIdRef = useRef(null);
  const soundEnabledRef = useRef(false);
  const audioContextRef = useRef(null);
  const lastSoundAtRef = useRef(0);
  const lastToastAtRef = useRef(0);
  const lastSoundNotificationKeyRef = useRef('');
  const hasPlayedFirstMutedSoundRef = useRef(false);
  const hasAccess = canUseSupportChatNotifications(user, token);

  useEffect(() => {
    latestUserIdRef.current = user?.id || null;
  }, [user]);

  useEffect(() => {
    if (!SUPPORT_CHAT_ADMIN_SOUND_ENABLED) {
      setSoundAlertsEnabledState(false);
      soundEnabledRef.current = false;
      return;
    }

    try {
      const storedValue = window.localStorage.getItem(SOUND_STORAGE_KEY);
      const enabled = storedValue === null ? true : storedValue === 'true';
      setSoundAlertsEnabledState(enabled);
      soundEnabledRef.current = enabled;
    } catch {
      setSoundAlertsEnabledState(true);
      soundEnabledRef.current = true;
    }
  }, []);

  useEffect(() => {
    soundEnabledRef.current = soundAlertsEnabled;
  }, [soundAlertsEnabled]);

  const resetState = useCallback(() => {
    setUnreadCount(0);
    setSessionUnreadCounts({});
    setLastSupportEvent(null);
  }, []);

  const setSoundAlertsEnabled = useCallback((enabled) => {
    if (!SUPPORT_CHAT_ADMIN_SOUND_ENABLED) {
      setSoundAlertsEnabledState(false);
      soundEnabledRef.current = false;
      return;
    }

    const nextValue = Boolean(enabled);
    setSoundAlertsEnabledState(nextValue);
    soundEnabledRef.current = nextValue;
    if (!nextValue) {
      hasPlayedFirstMutedSoundRef.current = false;
    }
    try {
      window.localStorage.setItem(SOUND_STORAGE_KEY, nextValue ? 'true' : 'false');
    } catch {
      // ignore
    }

    if (nextValue) {
      try {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) {
          return;
        }
        if (!audioContextRef.current) {
          audioContextRef.current = new AudioContextClass();
        }
        if (audioContextRef.current?.state === 'suspended') {
          audioContextRef.current.resume().catch(() => {});
        }
      } catch {
        // ignore
      }
    }
  }, []);

  const playBeep = useCallback((options = {}) => {
    const force = options.force === true;
    if (!SUPPORT_CHAT_ADMIN_SOUND_ENABLED || (!force && !soundEnabledRef.current)) {
      return;
    }

    const now = Date.now();
    if (now - lastSoundAtRef.current < SOUND_DUPLICATE_GUARD_MS) {
      return;
    }
    lastSoundAtRef.current = now;

    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) {
        return;
      }

      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContextClass();
      }

      const context = audioContextRef.current;
      if (context.state === 'suspended') {
        context.resume().catch(() => {});
      }

      const oscillator = context.createOscillator();
      const gainNode = context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = 880;
      gainNode.gain.value = 0.08;
      oscillator.connect(gainNode);
      gainNode.connect(context.destination);
      oscillator.start();
      oscillator.stop(context.currentTime + 0.14);
    } catch {
      // ignore
    }
  }, []);

  const getNotificationSoundKey = useCallback((notification) => {
    if (notification.id) {
      return `id:${notification.id}`;
    }

    return [
      notification.session_id || '',
      notification.notification_type || '',
      notification.created_at || notification.timestamp || '',
      notification.body || '',
    ].join('|');
  }, []);

  const playMessageNotificationSound = useCallback((notification) => {
    if (!notification) {
      return;
    }

    if (soundEnabledRef.current) {
      const notificationKey = getNotificationSoundKey(notification);
      if (notificationKey && notificationKey === lastSoundNotificationKeyRef.current) {
        return;
      }
      lastSoundNotificationKeyRef.current = notificationKey;
      playBeep();
      return;
    }

    if (!hasPlayedFirstMutedSoundRef.current) {
      hasPlayedFirstMutedSoundRef.current = true;
      playBeep({ force: true });
    }
  }, [getNotificationSoundKey, playBeep]);

  const refreshUnreadCount = useCallback(async () => {
    if (!hasAccess) {
      setUnreadCount(0);
      return 0;
    }

    try {
      const data = await supportChatService.getUnreadNotificationCount();
      const count = data?.unread_count || 0;
      setUnreadCount(count);
      return count;
    } catch (error) {
      console.error('Failed to refresh support chat unread count', error);
      return 0;
    }
  }, [hasAccess]);

  const applySupportEvent = useCallback((payload) => {
    if (!payload?.type) {
      return;
    }

    if (payload.type === 'support.notification.unread_count') {
      if (
        latestUserIdRef.current
        && payload.recipient_user_id
        && payload.recipient_user_id !== latestUserIdRef.current
      ) {
        return;
      }

      setUnreadCount(payload.unread_count || 0);
      setLastSupportEvent(payload);
      return;
    }

    const notification = payload.notification;
    if (!notification) {
      setLastSupportEvent(payload);
      return;
    }

    if (
      latestUserIdRef.current
      && notification.recipient_user_id
      && notification.recipient_user_id !== latestUserIdRef.current
    ) {
      return;
    }

    const sessionId = notification.session_id;
    if (sessionId) {
      setSessionUnreadCounts((prev) => {
        const currentValue = prev[sessionId] ?? 0;
        if (payload.type === 'support.notification.created') {
          return {
            ...prev,
            [sessionId]: currentValue + 1,
          };
        }
        if (payload.type === 'support.notification.read') {
          return {
            ...prev,
            [sessionId]: Math.max(0, currentValue - 1),
          };
        }
        return prev;
      });
    }

    if (payload.type === 'support.notification.created') {
      setUnreadCount((prev) => prev + 1);
    } else if (payload.type === 'support.notification.read') {
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }

    if (payload.type === 'support.notification.created') {
      const shouldAlertFor = new Set([
        'live_agent_requested',
        'new_waiting_message',
        'new_live_message',
      ]);

      if (shouldAlertFor.has(notification.notification_type)) {
        playMessageNotificationSound(notification);

        const now = Date.now();
        if (now - lastToastAtRef.current >= TOAST_DEBOUNCE_MS) {
          lastToastAtRef.current = now;
          setSnackbar({
            open: true,
            message: `${notification.title || 'New Support Chat'}${notification.body ? ` — ${notification.body}` : ''}`,
            severity: 'info',
          });
        }
      }
    }

    setLastSupportEvent(payload);
  }, [playMessageNotificationSound]);

  useEffect(() => {
    if (!hasAccess) {
      adminNotificationSocket.disconnect();
      resetState();
      setSocketStatus('idle');
      return undefined;
    }

    refreshUnreadCount();

    const unsubscribe = adminNotificationSocket.subscribe((event) => {
      if (event.kind === 'status') {
        setSocketStatus(event.status);
        return;
      }

      if (event.kind === 'message') {
        applySupportEvent(event.payload);
      }
    });

    adminNotificationSocket.connect();

    return () => {
      unsubscribe();
    };
  }, [applySupportEvent, hasAccess, refreshUnreadCount, resetState]);

  useEffect(() => {
    if (!hasAccess) {
      return undefined;
    }

    if (!['disconnected', 'idle'].includes(socketStatus)) {
      return undefined;
    }

    refreshUnreadCount();
    const intervalId = window.setInterval(() => {
      refreshUnreadCount();
    }, 60000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [hasAccess, refreshUnreadCount, socketStatus]);

  const markSessionNotificationsRead = useCallback(async (sessionId) => {
    const data = await supportChatService.markSessionNotificationsRead(sessionId);
    setUnreadCount(data?.unread_count || 0);
    setSessionUnreadCounts((prev) => ({
      ...prev,
      [sessionId]: 0,
    }));
    return data;
  }, []);

  const value = useMemo(() => ({
    unreadCount,
    socketStatus,
    socketConnected: socketStatus === 'connected',
    lastSupportEvent,
    sessionUnreadCounts,
    refreshUnreadCount,
    markSessionNotificationsRead,
    soundAlertsEnabled,
    setSoundAlertsEnabled,
  }), [
    lastSupportEvent,
    markSessionNotificationsRead,
    refreshUnreadCount,
    sessionUnreadCounts,
    socketStatus,
    unreadCount,
    setSoundAlertsEnabled,
    soundAlertsEnabled,
  ]);

  return (
    <SupportChatNotificationContext.Provider value={value}>
      {children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SupportChatNotificationContext.Provider>
  );
};


export const useSupportChatNotifications = () => {
  const context = useContext(SupportChatNotificationContext);
  if (!context) {
    throw new Error('useSupportChatNotifications must be used within a SupportChatNotificationProvider');
  }
  return context;
};
